import { useCallback } from 'react';

import { getQueryParamValue } from 'src/lib/browser';
import { useHistory } from 'src/lib/hooks/useHistory';
import { formatProjectCreateRoute } from 'src/lib/routing';
import { ROUTE_PARAMS } from 'src/routes';
import { useCustomerActions, useProjectActions } from 'src/store';
import { useQuotaEndedPopup } from 'src/views/QuotaEndedPopup';

const prepareRouteParams = (options) => {
  const params = {};
  if (options?.generationTool) {
    params[ROUTE_PARAMS.generationTool] = options.generationTool;
  }
  if (options?.improveContentType) {
    params[ROUTE_PARAMS.improveContentType] = options.improveContentType;
  }
  if (options?.subTemplateId) {
    params[ROUTE_PARAMS.subTemplateId] = options.subTemplateId;
  }
  if (options?.customMultiPromptId) {
    params[ROUTE_PARAMS.customMultiPromptId] = options.customMultiPromptId;
  }
  if (options?.customMultiPromptGroup) {
    params[ROUTE_PARAMS.customMultiPromptGroup] = options.customMultiPromptGroup;
  }
  if (options?.freestyleGenerationToolId) {
    params[ROUTE_PARAMS.freestyleGenerationToolId] = options.freestyleGenerationToolId;
  }

  const folder = getQueryParamValue(ROUTE_PARAMS.folder);
  if (folder) {
    params[ROUTE_PARAMS.folder] = folder;
  }

  return params;
};

export const useCreateProject = () => {
  const history = useHistory();
  const showQuotaEndedPopup = useQuotaEndedPopup();

  const customerActions = useCustomerActions();
  const projectActions = useProjectActions();

  /**
   * Navigate user to creation of a new project for selected "project type" and, possibly, "asset type"
   * @param {PROJECT_TYPE} projectType - project type
   * @param {object} options - additional options
   */
  return useCallback(
    (projectType, options = null) => {
      if (customerActions.isCurrentQuotaEnded()) {
        showQuotaEndedPopup();
        return;
      }

      if (options?.promptData) {
        projectActions.updateNewProjectPromptData(options?.promptData);
      }

      // If the prompt was already classified (user chose "Stay with prompt"),
      // store the prompt text to skip re-classification if unchanged
      if (options?.preClassifiedPromptText) {
        projectActions.setPreClassifiedPromptText(options.preClassifiedPromptText);
      } else {
        projectActions.clearPreClassifiedPromptText();
      }

      // If a generation tool override is provided (user chose "Stay with prompt" but we should generate
      // with the classified template's tool), store it for the project initialization
      if (options?.generationToolOverride) {
        projectActions.setNewProjectGenerationToolOverride(options.generationToolOverride);
      } else {
        projectActions.clearNewProjectGenerationToolOverride();
      }

      // Navigate to the "create project" page
      const params = prepareRouteParams(options);
      history.push(formatProjectCreateRoute(projectType, params), {
        state: { textToImprove: options?.textToImprove, keywords: options?.keywords },
      });
    },
    [customerActions, history, showQuotaEndedPopup, projectActions]
  );
};
